// @flow
//
import * as React from 'react';
import { connect } from 'react-redux';
import { getItem } from 'utils/localStorage';
import styled from 'styled-components';
import BaseLayout from 'layouts/BaseLayout';
import PageHeader from 'components/PageHeader';
import ResumeDatabaseBanner from 'components/ResumeDatabaseBanner';
import PageFooter from 'components/PageFooter';
import PrintHidden from 'components/PrintHidden';
import SearchMenuComponent from 'components/SearchMenu';
import SearchHero from 'components/SearchHero';
import Button from 'components/Button';
import Icon from 'components/Icon';
import NoAmp from 'containers/NoAmp';
import AmpContext from 'utils/AmpContext';
import HideContainer from 'containers/HideContainer';
import { isBrowser } from 'utils/browser';
import { isDefined } from 'utils/functional';
import Box from 'components/Box';
import { H4 } from 'components/Text';
import { trackEvent } from 'utils/analytics';
import { getListCookie, deleteCookie } from 'utils/cookies';
import type { CurrentUser } from 'api/currentUser';
import type { HeroSearchPartialRender, Layout as CmsLayout } from 'api/cms';
import { setWithSearchHero as reduxSetWithSearchHero } from '../redux/main/actions/layout';
import { colors, transition, gutterWidth, shadows } from '../theme';

const { useEffect, useContext } = React;

const testMode = getItem('testMode');

const StyledStickyBox = styled(Box)`
  ${!testMode &&
    `
    transform: translateZ(0);
    position: sticky;
    top: 0;
    z-index: 100;
    width: 100%;
  `}
`;

const SearchMenuContainer = styled(Box)`
  @media print {
    display: none;
  }
`;

const PageHeaderContainer = styled.div.attrs(props => ({
  style: {
    width: '100%',
    left: 0,
    top: 0,
    position: props.exposeSiteNav ? 'fixed' : 'relative',
    zIndex: props.exposeSiteNav ? 9999 : 102,
    boxShadow: props.exposeSiteNav ? '0 2px 4px rgba(0,0,0,0.15)' : null,
    pointerEvents: 'none'
  }
}))`
  /* Flow */
`;

type OwnProps = {
  buttonBar?: React.Node,
  onHomePage?: boolean,
  onCms?: boolean,
  onStaffDashboard?: boolean,
  url?: Object,
  navSection?: 'MAIN' | 'STAFF',
  defaultWithSearchHero?: boolean,
  layout?: CmsLayout,
  searchHeroHasCta?: boolean,
  children?: React.Node,
  searchMode?: boolean,
  setUserInitiatedSearch?: Function,
  defaultSearchType?: ListingType,
  onSearch?: Function,
  postSearchToolbar?: React.Node,
  darkMode?: boolean,
  showListingCounts?: boolean,
  hideFooterDivider?: boolean,
  toolbar?: React.Node,
  staffTools?: ?React.Node,
  hero?: HeroSearchPartialRender,
  exposeSiteNav?: boolean
};

type Props = OwnProps & {
  withSearchHero: boolean,
  setWithSearchHero: Function,
  currentUser: CurrentUser
};

const Layout = ({
  buttonBar,
  url,
  navSection = 'MAIN',
  defaultWithSearchHero,
  layout,
  searchHeroHasCta,
  children,
  searchMode,
  setUserInitiatedSearch,
  defaultSearchType,
  onSearch,
  postSearchToolbar,
  darkMode,
  showListingCounts,
  hideFooterDivider,
  toolbar,
  staffTools,
  hero,
  withSearchHero,
  setWithSearchHero,
  currentUser,
  exposeSiteNav,
  ...props
}: Props) => {
  const [searchOpen, setSearchOpen] = React.useState(false);
  const [pageUrlIsSet, setPageUrlIsSet] = React.useState(false);
  const [pageUrl, setPageUrl] = React.useState({
    en: '',
    es: '',
    pt: ''
  });
  const heroRef = React.useRef(null);

  const ampEnabled = useContext(AmpContext);

  const queuedTrackers = getListCookie('trackEvents');
  useEffect(
    () => {
      queuedTrackers.forEach(e => trackEvent(e.type));
      deleteCookie('trackEvents');
    },
    [queuedTrackers]
  );

  useEffect(
    () => {
      if (
        isDefined(defaultWithSearchHero) &&
        defaultWithSearchHero !== withSearchHero
      ) {
        setWithSearchHero(defaultWithSearchHero);
      }
    },
    [setWithSearchHero, defaultWithSearchHero, withSearchHero]
  );

  useEffect(
    () => {
      if (isBrowser() && !pageUrlIsSet) {
        const urlToSet = url || {
          en: window.location.pathname,
          es: window.location.pathname,
          pt: window.location.pathname
        };

        setPageUrl(urlToSet);
        setPageUrlIsSet(true);
      }
    },
    [pageUrl, pageUrlIsSet, setPageUrl, setPageUrlIsSet, url]
  );

  const toggleSearch = (open?: boolean = !searchOpen) => {
    setSearchOpen(open);
  };

  const SearchMenu = ({
    height,
    onSearch: searchHandler
  }: {
    height?: any,
    onSearch?: Function
  }) => {
    const search = () => {
      if (onSearch) {
        onSearch();
      }
      if (searchHandler) {
        searchHandler();
      }
    };
    return (
      <SearchMenuContainer height={height}>
        <SearchMenuComponent
          searchMode={searchMode}
          navSection={navSection}
          onSearch={search}
          defaultSearchType={defaultSearchType}
          inPageHeader
        />
      </SearchMenuContainer>
    );
  };

  const StaffTools = () =>
    staffTools ? (
      <Box
        position="absolute"
        top="100%"
        right={gutterWidth / 2}
        mt={[gutterWidth / 2, null, null, gutterWidth + 18]}
      >
        {staffTools}
      </Box>
    ) : null;

  const FixedStaffTools = () =>
    staffTools ? (
      <Box
        position="fixed"
        zIndex={102}
        top={[60 + gutterWidth / 2, null, null, 190]}
        right={gutterWidth / 2}
      >
        {staffTools}
      </Box>
    ) : null;

  const renderWithSearchHero = isDefined(defaultWithSearchHero)
    ? defaultWithSearchHero
    : withSearchHero;

  const hideHeroSearch = isDefined(searchMode) && searchMode && !ampEnabled;

  return (
    <BaseLayout
      buttonBar={buttonBar}
      stickyHeaderHeight={
        isDefined(postSearchToolbar)
          ? [135, 135, 135, 75]
          : [60, 60, 60, searchMode || !renderWithSearchHero ? 83 : null]
      }
      header={
        <>
          <Box mt={[60, null, null, 0]} mb={[-60, null, null, 0]}>
            <ResumeDatabaseBanner currentUser={currentUser} />
          </Box>
          <PageHeaderContainer exposeSiteNav={exposeSiteNav}>
            <Box>
              <PrintHidden>
                <PageHeader
                  url={pageUrl}
                  navSection={navSection}
                  ampEnabled={ampEnabled}
                  withSearchHero={renderWithSearchHero && !exposeSiteNav}
                  searchHeroHasCta={searchHeroHasCta}
                  toggleSearch={toggleSearch}
                />
              </PrintHidden>
            </Box>
          </PageHeaderContainer>
          {(!hero || searchMode) && !renderWithSearchHero ? (
            <NoAmp>
              {isDefined(postSearchToolbar) ? (
                <>
                  <HideContainer xs sm md position="relative" zIndex="101">
                    <SearchMenu />
                  </HideContainer>
                  <Box
                    component={StyledStickyBox}
                    display={['none', null, null, 'block']}
                  >
                    {postSearchToolbar}
                    <StaffTools />
                  </Box>
                  <Box height={75} display={['block', null, null, 'none']}>
                    <Box
                      position="fixed"
                      top={60}
                      left={0}
                      width={1}
                      zIndex="101"
                    >
                      {postSearchToolbar}
                      <StaffTools />
                    </Box>
                  </Box>
                </>
              ) : (
                <>
                  <FixedStaffTools />
                  <HideContainer xs sm md noWrapper>
                    <StyledStickyBox>
                      <Box boxShadow={shadows.toolbar}>
                        <SearchMenu />
                      </Box>
                    </StyledStickyBox>
                  </HideContainer>
                </>
              )}
            </NoAmp>
          ) : (
            <FixedStaffTools />
          )}
        </>
      }
    >
      {toolbar}

      {hero && (
        <Box
          position="relative"
          zIndex={2}
          height={hideHeroSearch ? 0 : 'auto'}
          overflow={hideHeroSearch && 'hidden'}
          bg={darkMode && colors.fog}
        >
          <div ref={heroRef}>
            <SearchHero
              title={hero.text}
              image={hero.image}
              imageMobile={hero.imageMobile}
              searchMode={hideHeroSearch}
              navSection={navSection}
              onSearch={onSearch}
              defaultSearchType={defaultSearchType}
              showListingCounts={showListingCounts}
              parallax={layout === 'FULL_WIDTH_PLUS'}
              pageUrl={pageUrl[USER_LOCALE]}
            />
          </div>
        </Box>
      )}

      <PrintHidden>
        <HideContainer lg xl>
          <Box
            position="fixed"
            top={0}
            left={0}
            zIndex="102"
            transform={
              searchOpen
                ? 'translateY(0)'
                : 'translateY(-100%) translateY(-6px)'
            }
            width="100%"
            height="100%"
            transition={`all ${transition}`}
          >
            <Box
              classes={['flex', 'align-center', 'justify-between']}
              position="absolute"
              left={0}
              top={0}
              zIndex={1}
              px={gutterWidth / 2}
              height={60}
              width="100%"
              borderBottom={`1px solid ${colors.aluminum}`}
            >
              <H4 mb={0} color={colors.waterBlue}>
                {getText('Search')}
              </H4>

              <Button
                onClick={() => setSearchOpen(false)}
                aria-label={getText('Close search')}
                ml="auto"
              >
                <Icon
                  name="close"
                  display="block"
                  width={16}
                  height={16}
                  stroke={colors.waterBlue}
                />
              </Button>
            </Box>
            <SearchMenu
              searchMode={searchMode}
              height="100%"
              onSearch={() => setSearchOpen(false)}
            />
          </Box>
        </HideContainer>
      </PrintHidden>

      <Box
        position="relative"
        flex="1 1 auto"
        bg={darkMode && colors.fog}
        pb={[36, null, null, 54]}
        mb={darkMode && '-1px'}
        borderBottom={darkMode && `1px solid ${colors.aluminum}`}
        {...props}
      >
        {children}
      </Box>
      <PrintHidden>
        <PageFooter url={pageUrl} hideDivider={hideFooterDivider} />
      </PrintHidden>
    </BaseLayout>
  );
};

const mapStateToProps = ({ layout, currentUser }) => ({
  withSearchHero: layout.withSearchHero,
  currentUser
});

export default connect<Props, OwnProps, _, _, _, _>(
  mapStateToProps,
  { setWithSearchHero: reduxSetWithSearchHero }
)(Layout);
